import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AgGridReact } from "ag-grid-react";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import {
  fetchHotelOrders,
  clearHotelOrders,
  fetchCondoOrders,
  clearCondoOrders,
} from "../../../redux/features/sales/payoutsSlice";
import { fetchHotels } from "../../../redux/features/hotels/hotelsSlice";
import { fetchCondos } from "../../../redux/features/condos/condosSlice";
import PayoutPreviewDialog from "./PayoutPreviewDialog";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  hotelSelect: {
    minWidth: 260,
  },
  selectionSummary: {
    padding: theme.spacing(2, 3),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.primary.main,
    color: theme.palette.type === "dark" ? "inherit" : "#fff",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  selectionSummaryText: {
    color:
      theme.palette.type === "dark"
        ? theme.palette.text.secondary
        : "rgba(255,255,255,0.8)",
  },
  previewBtn: {
    marginTop: theme.spacing(2),
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 200,
  },
}));

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const PayoutNewPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { mode } = useSelector((state) => state.ui);
  const { hotelsList } = useSelector((state) => state.hotels);
  const { condosList } = useSelector((state) => state.condos);
  const {
    hotelOrders,
    hotelCommissionPct,
    isLoadingOrders,
    errorOrders,
    condoOrders,
    condoCommissionPct,
    isLoadingCondoOrders,
    errorCondoOrders,
  } = useSelector((state) => state.payouts);

  const [propertyType, setPropertyType] = useState("hotel");
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [selectedCondoId, setSelectedCondoId] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const gridApiRef = useRef(null);

  const selectedHotel = useMemo(
    () =>
      propertyType === "hotel"
        ? hotelsList.find((h) => h.id === selectedHotelId) || null
        : null,
    [hotelsList, selectedHotelId, propertyType]
  );

  const selectedCondo = useMemo(
    () =>
      propertyType === "condo"
        ? condosList.find((c) => c.condos_id === selectedCondoId) || null
        : null,
    [condosList, selectedCondoId, propertyType]
  );

  useEffect(() => {
    dispatch(fetchHotels());
    dispatch(fetchCondos());
    return () => {
      dispatch(clearHotelOrders());
      dispatch(clearCondoOrders());
    };
  }, [dispatch]);

  useEffect(() => {
    if (propertyType === "hotel" && selectedHotelId) {
      dispatch(fetchHotelOrders(selectedHotelId, "unpaid"));
    } else {
      dispatch(clearHotelOrders());
    }
  }, [dispatch, propertyType, selectedHotelId]);

  useEffect(() => {
    if (propertyType === "condo" && selectedCondoId) {
      dispatch(fetchCondoOrders(selectedCondoId, "unpaid"));
    } else {
      dispatch(clearCondoOrders());
    }
  }, [dispatch, propertyType, selectedCondoId]);

  const rowData = useMemo(
    () => (propertyType === "hotel" ? hotelOrders || [] : condoOrders || []),
    [propertyType, hotelOrders, condoOrders]
  );
  const isLoadingOrdersActive =
    propertyType === "hotel" ? isLoadingOrders : isLoadingCondoOrders;
  const errorOrdersActive =
    propertyType === "hotel" ? errorOrders : errorCondoOrders;
  const errorMessage = errorOrdersActive?.errorMessage || "";
  const rowCount = rowData.length;

  const OrderIdCellRenderer = useCallback(
    (params) => {
      const id = params.value;
      if (!id) return null;
      return (
        <a
          href={`/sales/orders/${id}`}
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
              e.preventDefault();
              history.push(`/sales/orders/${id}`);
            }
          }}
          style={{
            textDecoration: "none",
            color: "inherit",
            fontWeight: "bold",
          }}
        >
          {id}
        </a>
      );
    },
    [history]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        minWidth: 50,
        pinned: "left",
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
        cellRendererFramework: OrderIdCellRenderer,
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        pinned: "left",
        minWidth: 150,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 150,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Package",
        field: "package_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
      },
      {
        headerName: "Room",
        field: "room_name",
        filter: "agTextColumnFilter",
        minWidth: 140,
      },
      {
        headerName: "Rates Subtotal",
        field: "rates_subtotal",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Addons Subtotal",
        field: "addons_subtotal",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Tax",
        field: "tax",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Resort Fee",
        field: "resort_fee_total",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Order Total",
        field: "total",
        type: "rightAligned",
        pinned: "right",
        width: 140,
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
        cellStyle: { fontWeight: "bold" },
      },
    ],
    [OrderIdCellRenderer]
  );

  const condoColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        minWidth: 50,
        pinned: "left",
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
        cellRendererFramework: OrderIdCellRenderer,
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 150,
      },
      {
        headerName: "Condo",
        field: "condo_name",
        filter: "agTextColumnFilter",
        minWidth: 150,
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
      },
      {
        headerName: "Taxable Base",
        field: "taxable_base",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Commission",
        field: "commission",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Taxes",
        field: "taxes",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
      },
      {
        headerName: "Transfer Amount",
        field: "transfer_amount",
        type: "rightAligned",
        pinned: "right",
        width: 160,
        valueFormatter: (params) =>
          params.value !== null
            ? currencyFormatter.format(Number(params.value))
            : "",
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
    ],
    [OrderIdCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 110,
    }),
    []
  );

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;
      if (isLoadingOrdersActive) params.api.showLoadingOverlay();
      else if (!rowCount || errorOrdersActive) params.api.showNoRowsOverlay();
    },
    [errorOrdersActive, isLoadingOrdersActive, rowCount]
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (isLoadingOrdersActive) {
      api.showLoadingOverlay();
      return;
    }
    if (errorOrdersActive || rowCount === 0) {
      api.showNoRowsOverlay();
      return;
    }
    api.hideOverlay();
  }, [errorOrdersActive, isLoadingOrdersActive, rowCount]);

  const onSelectionChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    setSelectedRows(api.getSelectedRows());
  }, []);

  const previewSummary = useMemo(() => {
    if (propertyType === "hotel") {
      if (!selectedHotel || selectedRows.length === 0) return null;
      const commissionPct = Number(hotelCommissionPct) || 0;
      const orders = selectedRows.map((row) => ({
        order_id: row.id,
        customer_name: row.customer_name,
        date_order_placed: row.date_order_placed,
        check_in: row.check_in,
        check_out: row.check_out,
        taxable_base: Number(row.taxable_base || 0),
        commission: Number(row.commission || 0),
        taxes: Number(row.taxes || 0),
        resort_fee: Number(row.resort_fee || 0),
        transfer_amount: Number(row.transfer_amount || 0),
        total: Number(row.total || 0),
      }));
      const totalOrdersAmount = orders.reduce((s, o) => s + o.total, 0);
      const totalCommission = orders.reduce((s, o) => s + o.commission, 0);
      const totalTransfer = totalOrdersAmount - totalCommission;
      return {
        payoutType: "hotel",
        hotel: selectedHotel,
        commissionPct,
        orders,
        totalOrdersAmount,
        totalCommission,
        totalTransfer,
      };
    } else {
      if (!selectedCondo || selectedRows.length === 0) return null;
      const commissionPct = Number(condoCommissionPct) || 0;
      const orders = selectedRows.map((row) => ({
        order_id: row.id,
        customer_name: row.customer_name,
        condo_name: row.condo_name,
        check_in: row.check_in,
        check_out: row.check_out,
        taxable_base: Number(row.taxable_base || 0),
        commission: Number(row.commission || 0),
        taxes: Number(row.taxes || 0),
        transfer_amount: Number(row.transfer_amount || 0),
      }));
      const totalOrdersAmount = orders.reduce(
        (s, o) => s + o.taxable_base + o.taxes,
        0
      );
      const totalCommission = orders.reduce((s, o) => s + o.commission, 0);
      const totalTransfer = orders.reduce((s, o) => s + o.transfer_amount, 0);
      return {
        payoutType: "condo",
        condo: selectedCondo,
        commissionPct,
        orders,
        totalOrdersAmount,
        totalCommission,
        totalTransfer,
      };
    }
  }, [
    propertyType,
    selectedHotel,
    selectedCondo,
    selectedRows,
    hotelCommissionPct,
    condoCommissionPct,
  ]);

  const handlePayoutSuccess = useCallback(
    (payoutData) => {
      setPreviewOpen(false);
      history.push(`/sales/payouts/${payoutData.payout_id}`, {
        showSuccess: true,
        payoutType: propertyType,
        snackbarContent: `Payout #${
          payoutData.payout_id
        } created successfully for ${currencyFormatter.format(
          payoutData.total_transfer
        )}`,
      });
    },
    [history, propertyType]
  );

  const selectedPropertyId =
    propertyType === "hotel" ? selectedHotelId : selectedCondoId;
  const selectedPropertyName =
    propertyType === "hotel" ? selectedHotel?.name : selectedCondo?.name;

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/sales/payouts")}
          >
            Back to Payouts
          </Button>
          <Typography variant="h6">New Payout</Typography>
        </Grid>

        {/* Step 1: Property Type */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Step 1: Property Type
            </Typography>
            <ToggleButtonGroup
              value={propertyType}
              exclusive
              size="small"
              onChange={(_, val) => {
                if (!val) return;
                setPropertyType(val);
                setSelectedHotelId("");
                setSelectedCondoId("");
                setSelectedRows([]);
              }}
              style={{ marginBottom: 16 }}
            >
              <ToggleButton value="hotel">Hotel</ToggleButton>
              <ToggleButton value="condo">Condo</ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>

        {/* Step 2: Select Hotel or Condo */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Step 2: Select {propertyType === "hotel" ? "Hotel" : "Condo"}
            </Typography>
            {propertyType === "hotel" ? (
              <FormControl
                variant="outlined"
                size="small"
                className={classes.hotelSelect}
              >
                <InputLabel>Hotel</InputLabel>
                <Select
                  value={selectedHotelId}
                  onChange={(e) => {
                    setSelectedRows([]);
                    setSelectedHotelId(e.target.value);
                  }}
                  label="Hotel"
                >
                  <MenuItem value="">
                    <em>Select a hotel</em>
                  </MenuItem>
                  {hotelsList.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl
                variant="outlined"
                size="small"
                className={classes.hotelSelect}
              >
                <InputLabel>Condo</InputLabel>
                <Select
                  value={selectedCondoId}
                  onChange={(e) => {
                    setSelectedRows([]);
                    setSelectedCondoId(e.target.value);
                  }}
                  label="Condo"
                >
                  <MenuItem value="">
                    <em>Select a condo</em>
                  </MenuItem>
                  {condosList.map((c) => (
                    <MenuItem key={c.condos_id} value={c.condos_id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Paper>
        </Grid>

        {/* Orders Grid */}
        {selectedPropertyId && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Step 3: Select Eligible Orders
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Showing COMPLETE, unpaid orders for{" "}
                <strong>{selectedPropertyName}</strong>. Check the orders to
                include in this payout.
              </Typography>

              {isLoadingOrdersActive ? (
                <div className={classes.loadingOverlay}>
                  <CircularProgress />
                </div>
              ) : (
                <div
                  className={
                    mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
                  }
                  style={{ height: 500, width: "100%" }}
                >
                  <AgGridReact
                    rowData={rowData}
                    columnDefs={
                      propertyType === "hotel" ? columnDefs : condoColumnDefs
                    }
                    defaultColDef={defaultColDef}
                    rowSelection="multiple"
                    suppressRowClickSelection
                    enableCellTextSelection
                    animateRows
                    overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading orders...</span>"
                    overlayNoRowsTemplate={
                      errorMessage
                        ? `<span class='ag-overlay-no-rows-center'>${errorMessage}</span>`
                        : "<span class='ag-overlay-no-rows-center'>No eligible orders found</span>"
                    }
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                  />
                </div>
              )}

              {/* Selection Summary */}
              {selectedRows.length > 0 && previewSummary && (
                <div className={classes.selectionSummary}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 700, marginBottom: 4 }}
                      >
                        <strong>{selectedRows.length}</strong> order
                        {selectedRows.length !== 1 ? "s" : ""} selected
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.selectionSummaryText}
                      >
                        Orders Total:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalOrdersAmount
                          )}
                        </strong>
                        {"  ·  "}
                        Our Commission ({previewSummary.commissionPct}%)*:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalCommission
                          )}
                        </strong>
                        {"  ·  "}
                        Transfer:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalTransfer
                          )}
                        </strong>
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.selectionSummaryText}
                        style={{ opacity: 0.75 }}
                      >
                        * Commission applies to rates &amp; addons subtotal only
                        — taxes and resort fees excluded.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
                      <Button
                        variant="outlined"
                        style={{ borderColor: "white", color: "white" }}
                        onClick={() => setPreviewOpen(true)}
                      >
                        Preview &amp; Confirm Payout
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}

              {selectedRows.length === 0 &&
                !isLoadingOrdersActive &&
                rowCount > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 16 }}
                  >
                    Select at least one order to continue.
                  </Typography>
                )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {previewSummary && (
        <PayoutPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          summary={previewSummary}
          onSuccess={handlePayoutSuccess}
        />
      )}
    </>
  );
};

export default PayoutNewPage;
